// Per-language title + description editor shared by the zone & exhibit forms.
// A thin adapter over the shared <LocalizedContentTabs>: the orchestrator owns
// formData and passes titleTc/…/descriptionSc (camelCase per ADR-0020); this
// maps that to the casing-agnostic component and back. Title + description
// share a tab, so switching language switches both.

import type { FC } from "react";

import LocalizedContentTabs from "@shared/components/LocalizedContentTabs";
import type { ContentLanguage } from "@shared/lib/language";

export interface TranslationsFormData {
  titleTc: string;
  titleEn: string;
  titleSc: string;
  descriptionTc: string;
  descriptionEn: string;
  descriptionSc: string;
}

export interface TranslationsProps {
  formData: TranslationsFormData;
  onChange: (patch: Partial<TranslationsFormData>) => void;
  /** Read-only while the owner is cascade archived / purged (lockdown). */
  disabled?: boolean;
}

const TITLE_KEY: Record<ContentLanguage, keyof TranslationsFormData> = {
  tc: "titleTc",
  en: "titleEn",
  sc: "titleSc",
};

const DESC_KEY: Record<ContentLanguage, keyof TranslationsFormData> = {
  tc: "descriptionTc",
  en: "descriptionEn",
  sc: "descriptionSc",
};

const Translations: FC<TranslationsProps> = ({
  formData,
  onChange,
  disabled,
}) => (
  <LocalizedContentTabs
    titles={{
      tc: formData.titleTc,
      en: formData.titleEn,
      sc: formData.titleSc,
    }}
    descriptions={{
      tc: formData.descriptionTc,
      en: formData.descriptionEn,
      sc: formData.descriptionSc,
    }}
    onTitleChange={(lang: ContentLanguage, value: string) =>
      onChange({ [TITLE_KEY[lang]]: value })
    }
    onDescriptionChange={(lang: ContentLanguage, value: string) =>
      onChange({ [DESC_KEY[lang]]: value })
    }
    disabled={disabled}
  />
);

export default Translations;
